/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable import/no-cycle */
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, Divider, Popover } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { CheckboxOptionType, CheckboxValueType } from 'antd/lib/checkbox/Group';

import { usePrecalculateTariffMutation } from 'entities/tariff/api';
import {
	AdditionalOption,
	SubscriptionOrderFinal,
	SubscriptionResponse,
	TariffsResponse,
} from 'entities/tariff/api/contracts';
import { PricePerUnit } from 'entities/tariff/ui/PricePerUnits/PricePerUnits';
import { UserUnitItem } from 'entities/user/api/contracts';
import { BlockedUnitsNotificationStyled } from 'features/show-subscriptions/ui/LicensesTableUsers/LicensesTableUsers.styles';
import { SubscriptionEditOrder } from 'features/tariffs/edit/ui/EditModals/EditModalContainer/EditModalContainer';
import { UnitsDiff } from 'features/tariffs/edit/ui/UnitsDiff/UnitsDiff';
import {
	AdditionalPriceStyled,
	ChooseUnitsSelectedContainerStyled,
	ChooseUnitsTextStyled,
	FixedPriceContainerStyled,
	GridCheckboxGroupStyled,
	OrderInfoContainerStyled,
	OrderInfoItemStyled,
	PriceSummaryContainerStyled,
	PriceSummaryHeadingStyled,
	TermsOfServiceContainerStyled,
} from 'features/tariffs/subscribe/ui/SubscribeModals/SubscribeConfigure/SubscribeConfigure.styles';
import { SubscribePrice } from 'features/tariffs/subscribe/ui/SubscribePrice/SubscribePrice';
import {
	ChooseUnitsContainerStyled,
	ChooseUnitsSelectionHeadingStyled,
	MessageContainerStyled,
	OrderInfoItemTextStyled,
} from './EditConfigure.styles';

interface ChooseUnitsModalProps {
	plan: TariffsResponse;
	updateOrder: (newOrder: SubscriptionEditOrder) => void;
	order: SubscriptionEditOrder;
	units: UserUnitItem[];
	additionalFeatures: AdditionalOption[];
	subscription: SubscriptionResponse | null;
	toggleRulesAccepted: (n: boolean) => void;
}

export const EditConfigure: FC<ChooseUnitsModalProps> = ({
	plan,
	updateOrder,
	order,
	units,
	additionalFeatures,
	subscription,
	toggleRulesAccepted,
}) => {
	const CheckboxGroup = Checkbox.Group;

	const defaultCheckedList = subscription ? subscription.relatedEntityIds.map(n => n.id) : [];
	const defaultFeaturesCheckedList = subscription ? subscription.additionalOptions.map(n => n.id) : [];
	const { t } = useTranslation();
	const [checkedList, setCheckedList] = useState<CheckboxValueType[]>(defaultCheckedList);
	const [checkedFeaturesList, setCheckedFeaturesList] = useState<CheckboxValueType[]>(defaultFeaturesCheckedList);
	const [checkAll, setCheckAll] = useState(false);
	const [sendPrecalculate, { isLoading }] = usePrecalculateTariffMutation();
	const [blockedUnits, setBlockedUnits] = useState<string[]>([]);
	const [precalculatedOrder, setPrecalculatedOrder] = useState<number>(0);
	const [precalculatedPricePerUnit, setPrecalculatedPricePerUnit] = useState<number>(0);

	const hasSpecialPrice = plan.tariffOverrides.filter(n => n.type === 1).length !== 0;
	const hasFixedPrice = plan.tariffOverrides.find(n => n.type === 2);

	useEffect(() => {
		const fetchPrecalculate = async (): Promise<void> => {
			if (order.relatedEntityIds.length === 0) {
				setPrecalculatedOrder(0);
			} else {
				const subscriptionRequestBody: SubscriptionOrderFinal = {
					trialId: null,
					contactEmail: order.contactEmail,
					tariffId: order.tariffId,
					legalEntityId: order.legalEntityId.id,
					relatedEntityIds: order.relatedEntityIds,
					additionalOptions: order.additionalFeatures,
					payload: order.payload,
					addNewUnitOnOpening: order.addNewUnitOnOpening,
				};
				const res = await sendPrecalculate(subscriptionRequestBody).unwrap();
				setPrecalculatedOrder(res.result.price);
				setPrecalculatedPricePerUnit(res.result.pricePerUnit);
				setBlockedUnits(res.result.blockedRelatedEntityIds);
				setCheckAll(res.result.relatedEntityIds.length === units.length);
			}
		};
		void fetchPrecalculate();
	}, [order, sendPrecalculate, units.length]);

	const plainOptions: CheckboxOptionType[] = units.map(n => ({
		label: (
			<p
				style={{
					margin: `0`,
					color: blockedUnits.find(unitId => unitId === n.id) ? `#FF4D4F` : `#262626`,
				}}
			>
				{n.subscriptionUnitStatus === 1 ? `1` : n.name}
			</p>
		),
		value: n.id,
	}));

	const additionalFeaturesOptions: CheckboxOptionType[] = additionalFeatures.map(n => ({
		label: `${n.name} (+${n.price ? n.price : ``} ₽ за заведение)`,
		value: n.id,
	}));

	const getFeaturePriceById = (featureId: CheckboxValueType): number => {
		const feature = additionalFeatures.find(n => n.id === featureId);
		if (!feature || !feature.price) {
			return 0;
		}

		return feature.price;
	};

	const onChange = (list: CheckboxValueType[]): void => {
		setCheckedList(list);
		updateOrder({
			...order,
			relatedEntityIds: units.filter(unit => list.includes(unit.id)).map(n => n.id),
		});
		setCheckAll(list.length === plainOptions.length);
	};

	const onFeatureChange = (list: CheckboxValueType[]): void => {
		setCheckedFeaturesList(list);
		updateOrder({
			...order,
			additionalFeatures: list.map(String),
			additionalPriceForUnit: list.map(n => getFeaturePriceById(n)).reduce((a, b) => a + b, 0),
		});
	};

	const onCheckAllChange = (e: CheckboxChangeEvent): void => {
		setCheckedList(e.target.checked ? plainOptions.map(n => n.value) : []);
		updateOrder({
			...order,
			relatedEntityIds: e.target.checked ? units.map(n => n.id) : [],
		});
		setCheckAll(e.target.checked);
	};

	const clearAll = (): void => {
		setCheckedList([]);
		setCheckAll(false);
		updateOrder({
			...order,
			relatedEntityIds: [],
		});
	};

	const onChangeUnitsAutomaticallyChange = (e: CheckboxChangeEvent): void => {
		updateOrder({
			...order,
			addNewUnitOnOpening: e.target.checked,
		});
	};

	const onTermsOfServiceChange = (e: CheckboxChangeEvent): void => {
		toggleRulesAccepted(e.target.checked);
	};

	if (!plan.price) {
		return <div>error</div>;
	}

	return (
		<ChooseUnitsContainerStyled>
			<ChooseUnitsSelectionHeadingStyled>
				{t(`Marketplace_Select_Establishments`)}
			</ChooseUnitsSelectionHeadingStyled>

			<MessageContainerStyled>{t(`Marketplace_Edit_Message`)}</MessageContainerStyled>
			{subscription?.hasBlockedSubscriptions && (
				<BlockedUnitsNotificationStyled style={{ marginBottom: `16px` }}>
					<ExclamationCircleOutlined style={{ color: `#FF4D4F`, marginTop: `4px` }} />
					{t(`Marketplace_Units_Blocked`, {
						units: subscription.relatedEntityIds.filter(n => n.subscriptionUnitStatus === 1).length,
					})}
				</BlockedUnitsNotificationStyled>
			)}
			{plan.additionalOptions && plan.additionalOptions.length !== 0 && (
				<>
					<ChooseUnitsSelectionHeadingStyled>Дополнительные функции:</ChooseUnitsSelectionHeadingStyled>
					<CheckboxGroup
						options={additionalFeaturesOptions}
						onChange={onFeatureChange}
						value={checkedFeaturesList}
						style={{ flexDirection: `column`, display: `flex`, gap: `7px`, marginBottom: `36px` }}
					/>
					<Divider style={{ margin: `12px 0` }} />
				</>
			)}
			<>
				<ChooseUnitsSelectedContainerStyled>
					<Checkbox onChange={onCheckAllChange} checked={checkAll} style={{ marginBottom: `7px` }}>
						{t(`Marketplace_Select_All`)}
					</Checkbox>
					<Button type='link' onClick={clearAll} style={{ paddingRight: `0` }}>
						{t(`Marketplace_Clear_List`)}
					</Button>
				</ChooseUnitsSelectedContainerStyled>

				{plainOptions.length > 20 ? (
					<GridCheckboxGroupStyled options={plainOptions} onChange={onChange} value={checkedList} />
				) : (
					<CheckboxGroup
						options={plainOptions}
						onChange={onChange}
						value={checkedList}
						style={{
							flexDirection: `column`,
							display: `flex`,
							gap: `7px`,
							maxHeight: `200px`,
							flexWrap: `wrap`,
						}}
					/>
				)}

				{plan.addNewUnitOnOpening && (
					<Checkbox
						onChange={onChangeUnitsAutomaticallyChange}
						checked={order.addNewUnitOnOpening}
						style={{ margin: `16px 0` }}
					>
						{t(`Marketplace_Automatic_Addition_New_Establishment`)}
					</Checkbox>
				)}
			</>

			{order.relatedEntityIds.length !== 0 && precalculatedPricePerUnit !== 0 && (
				<>
					<Divider style={{ margin: `12px 0 0 0` }} />
					<OrderInfoContainerStyled>
						<OrderInfoItemStyled>
							<OrderInfoItemTextStyled>{t(`Marketplace_Selected`)}</OrderInfoItemTextStyled>
							<OrderInfoItemTextStyled>
								{`${checkedList.length} ${t(`Marketplace_Establishments`)}`}
								<UnitsDiff
									units={order.relatedEntityIds.length - (subscription?.relatedEntityIds.length || 0)}
								/>
							</OrderInfoItemTextStyled>
						</OrderInfoItemStyled>
						{!hasFixedPrice && plan.addNewUnitOnOpening && (
							<OrderInfoItemStyled>
								<OrderInfoItemTextStyled>
									{t(`Marketplace_Price_Per_Establishment`)}
									{!!plan.tariffOverrides.length && (
										<Popover
											content={
												<PricePerUnit
													overrides={plan.tariffOverrides.filter(n => n.type === 0)}
												/>
											}
											placement='bottom'
										>
											<QuestionCircleOutlined style={{ marginLeft: `4px` }} />
										</Popover>
									)}
								</OrderInfoItemTextStyled>
								<OrderInfoItemTextStyled>{`${precalculatedPricePerUnit} ₽ /${t(
									`Marketplace_Per_Month`,
								)}`}</OrderInfoItemTextStyled>
							</OrderInfoItemStyled>
						)}
						{hasFixedPrice && plan.addNewUnitOnOpening && (
							<OrderInfoItemStyled>
								<OrderInfoItemTextStyled>{t(`Marketplace_Price`)}</OrderInfoItemTextStyled>
								<FixedPriceContainerStyled>
									<OrderInfoItemTextStyled>
										{`${plan.tariffOverrides
											.filter(n => n.type === 2)[0]
											.price.toLocaleString()} ₽`}
									</OrderInfoItemTextStyled>
									{hasFixedPrice.toNumberOfUnits &&
										order.relatedEntityIds.length > hasFixedPrice.toNumberOfUnits && (
											<AdditionalPriceStyled>{`+ ${
												plan.price *
												(order.relatedEntityIds.length - hasFixedPrice.toNumberOfUnits)
											} ₽`}</AdditionalPriceStyled>
										)}
								</FixedPriceContainerStyled>
							</OrderInfoItemStyled>
						)}
					</OrderInfoContainerStyled>
				</>
			)}
			<Divider style={{ margin: `0` }} />
			<PriceSummaryContainerStyled>
				<PriceSummaryHeadingStyled>{t(`Marketplace_Total`)}</PriceSummaryHeadingStyled>
				<SubscribePrice
					price={order.relatedEntityIds.length === 0 ? 0 : precalculatedOrder}
					loading={isLoading}
					isTrialAvailable={plan.isTrialAvailable}
					trial={null}
					hasSpecialPrice={hasSpecialPrice}
				/>
			</PriceSummaryContainerStyled>
			<TermsOfServiceContainerStyled>
				<Checkbox onChange={onTermsOfServiceChange} />
				<ChooseUnitsTextStyled>
					{t(`Marketplace_Acceptance`)}
					{` `}
					<a
						href='https://docs.google.com/document/d/1sFPYQN75p5GvYytO2bKMDYOBCipDUSXq/edit'
						target='_blank'
						rel='noreferrer'
					>
						{t(`Marketplace_User_Agreement`).toLocaleLowerCase()}
					</a>
					{` `}
					{t(`Marketplace_Obligation_To_Pay_Invoice`)}
				</ChooseUnitsTextStyled>
			</TermsOfServiceContainerStyled>
		</ChooseUnitsContainerStyled>
	);
};
